// Twilio setup + one-click auto-configure. Lives under Voice & Chat (trunk
// provisioning belongs with trunks, not Integrations). Connect the account,
// discover DIDs, and Configure — provisions the Twilio Elastic SIP Trunk and the
// matching Stratus trunk / inbound DID routes / outbound rules in one shot.
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, CheckCircle2, AlertCircle, Eye, EyeOff, Trash, Phone } from 'lucide-react';
import { getToken } from '../lib/api';
import { getActiveTenant, ALL } from '../lib/tenantScope';
import { useToast } from '../components/Toast';

const auth = () => ({ Authorization: `Bearer ${getToken()}` });

// The "Viewing: <department>" selection (top bar) — Twilio credentials are
// per-department, so every call is scoped to it. Empty = the admin's own domain.
const scopedDomain = (): string => { const a = getActiveTenant(); return a !== ALL ? a : ''; };
const scopeQs = (): string => { const d = scopedDomain(); return d ? `?domain_uuid=${encodeURIComponent(d)}` : ''; };
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...auth(), ...(init.headers || {}) },
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

export default function TwilioSetup() {
  const qc = useQueryClient();
  const toast = useToast();
  const sd = scopedDomain();
  const cfg = useQuery<any>({ queryKey: ['integrations-twilio', sd], queryFn: () => fJson('/api/integrations/twilio' + scopeQs()) });

  const [authMethod, setAuthMethod] = useState<'auth_token' | 'api_key'>('auth_token');
  const [accountSid, setAccountSid] = useState('');
  const [authToken,  setAuthToken]  = useState('');
  const [apiKeySid,  setApiKeySid]  = useState('');
  const [apiSecret,  setApiSecret]  = useState('');
  const [showTok,    setShowTok]    = useState(false);
  const [showSec,    setShowSec]    = useState(false);

  const c = cfg.data;
  useEffect(() => {
    if (!c) return;
    setAuthMethod(c.auth_method === 'api_key' ? 'api_key' : 'auth_token');
    setAccountSid(c.account_sid || '');
    setApiKeySid(c.api_key_sid || '');
    // Secrets are never returned by the server; leave the inputs blank. A stored
    // secret shows as a placeholder and is only overwritten when the user types.
    setAuthToken('');
    setApiSecret('');
  }, [c]);

  // Only send a secret when the operator actually typed one — blank means "keep".
  function buildBody() {
    const body: any = {
      auth_method: authMethod,
      account_sid: accountSid.trim(),
      enabled: c?.enabled !== false,
    };
    if (authMethod === 'api_key') body.api_key_sid = apiKeySid.trim();
    if (authToken.trim()) body.auth_token = authToken.trim();
    if (apiSecret.trim()) body.api_secret = apiSecret.trim();
    if (sd) body.domain_uuid = sd;   // write to the selected department
    return body;
  }

  const save = useMutation({
    mutationFn: () => fJson('/api/integrations/twilio', { method: 'PUT', body: JSON.stringify(buildBody()) }),
    onSuccess: () => { toast('success', 'Saved'); qc.invalidateQueries({ queryKey: ['integrations-twilio'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  // Connect & Verify: persist first (so the token exists server-side), then probe
  // Twilio. One button — matches the "auto set up + prove it's live" ask.
  const connect = useMutation({
    mutationFn: async () => {
      await fJson('/api/integrations/twilio', { method: 'PUT', body: JSON.stringify(buildBody()) });
      return fJson('/api/integrations/twilio/verify' + scopeQs(), { method: 'POST' });
    },
    onSuccess: (r: any) => {
      toast(r?.ok ? 'success' : 'error', r?.message || (r?.ok ? 'Connected' : 'Verification failed'));
      qc.invalidateQueries({ queryKey: ['integrations-twilio'] });
    },
    onError: (e: any) => toast('error', String(e.message || e)),
  });

  const disconnect = useMutation({
    mutationFn: () => fJson('/api/integrations/twilio' + scopeQs(), { method: 'DELETE' }),
    onSuccess: () => {
      toast('success', 'Disconnected');
      setAccountSid(''); setAuthToken(''); setApiKeySid(''); setApiSecret('');
      qc.invalidateQueries({ queryKey: ['integrations-twilio'] });
    },
    onError: (e: any) => toast('error', String(e.message || e)),
  });

  const verified = !!c?.verified_at;
  const hasError = !!c?.verification_error;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Twilio</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={connect.isPending} onClick={() => connect.mutate()}>
          {connect.isPending ? 'Verifying…' : <><CheckCircle2 size={14} /> Connect &amp; Verify</>}
        </button>
      </div>

      {/* Connection status banner */}
      {c?.configured && (
        <div className={`card p-3 flex items-center gap-2 text-sm ${
          verified ? 'text-success' : hasError ? 'text-danger' : 'text-textSecondary'
        }`}>
          {verified ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {verified ? (
            <span>
              Connected{c.friendly_name ? ` to “${c.friendly_name}”` : ''} — last verified {new Date(c.verified_at).toLocaleString()}
            </span>
          ) : hasError ? (
            <span>{c.verification_error}</span>
          ) : (
            <span>Credentials saved but not yet verified — click Connect &amp; Verify.</span>
          )}
        </div>
      )}

      <div className="card p-4 space-y-4">
        <p className="text-sm text-textSecondary">
          Connect your Twilio account once here. The Twilio trunk auto-setup reuses these
          credentials, so you don't re-enter them per trunk. Secrets are encrypted at rest and
          never shown again after saving.
        </p>

        <div>
          <label className="block text-sm text-textSecondary mb-1">Authentication method</label>
          <select className="input" value={authMethod} onChange={e => setAuthMethod(e.target.value as any)}>
            <option value="auth_token">Account SID + Auth Token</option>
            <option value="api_key">API Key SID + Secret (recommended — revocable)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm text-textSecondary mb-1">Account SID</label>
          <input className="input font-mono" value={accountSid} onChange={e => setAccountSid(e.target.value)}
                 placeholder="AC################################" />
          <div className="text-xs text-textSecondary mt-1">
            Found on your Twilio Console dashboard. Always required — it identifies the account.
          </div>
        </div>

        {authMethod === 'auth_token' ? (
          <div>
            <label className="block text-sm text-textSecondary mb-1">Auth Token</label>
            <div className="relative">
              <input className="input pr-10 font-mono" type={showTok ? 'text' : 'password'}
                     value={authToken} onChange={e => setAuthToken(e.target.value)}
                     placeholder={c?.has_auth_token ? '•••••••• (stored — leave blank to keep)' : 'Twilio Auth Token'} />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 text-textSecondary"
                      onClick={() => setShowTok(s => !s)} title={showTok ? 'Hide' : 'Show'}>
                {showTok ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm text-textSecondary mb-1">API Key SID</label>
              <input className="input font-mono" value={apiKeySid} onChange={e => setApiKeySid(e.target.value)}
                     placeholder="SK################################" />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">API Key Secret</label>
              <div className="relative">
                <input className="input pr-10 font-mono" type={showSec ? 'text' : 'password'}
                       value={apiSecret} onChange={e => setApiSecret(e.target.value)}
                       placeholder={c?.has_api_secret ? '•••••••• (stored — leave blank to keep)' : 'API Key Secret'} />
                <button className="absolute right-2 top-1/2 -translate-y-1/2 text-textSecondary"
                        onClick={() => setShowSec(s => !s)} title={showSec ? 'Hide' : 'Show'}>
                  {showSec ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <div className="text-xs text-textSecondary mt-1">
                Create under Twilio Console → Account → API keys &amp; tokens. Revoke anytime without changing your Auth Token.
              </div>
            </div>
          </>
        )}

        {c?.configured && (
          <button className="btn-ghost text-sm inline-flex items-center gap-1 text-danger"
                  disabled={disconnect.isPending}
                  onClick={() => { if (confirm('Disconnect Twilio and delete the stored credentials?')) disconnect.mutate(); }}>
            <Trash size={14} /> Disconnect
          </button>
        )}
      </div>

      {c?.configured && <TwilioNumbersCard verified={verified} sd={sd} />}
    </div>
  );
}

// Number discovery + one-click auto-configure. Select DIDs, choose where inbound
// calls ring, optionally generate outbound rules, then Configure — this provisions
// the Twilio Elastic SIP Trunk (IP ACL + origination + attach numbers) AND the
// Stratus trunk / inbound DID routes / outbound rules in one shot.
function TwilioNumbersCard({ verified, sd }: { verified: boolean; sd: string }) {
  const toast = useToast();
  const qs = sd ? `?domain_uuid=${encodeURIComponent(sd)}` : '';
  const [loaded, setLoaded] = useState(false);
  const nums = useQuery<any>({
    queryKey: ['integrations-twilio-numbers', sd],
    queryFn: () => fJson('/api/integrations/twilio/numbers' + qs),
    enabled: loaded, retry: false,
  });
  const dests = useQuery<any[]>({
    queryKey: ['call-handling-destinations', sd],
    queryFn: () => fJson('/api/call-handling/destinations' + qs),
    enabled: loaded,
  });

  const list: any[] = nums.data?.numbers || [];
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [trunkName, setTrunkName] = useState('');
  const [inbound, setInbound] = useState('end_call');
  const [mkOutbound, setMkOutbound] = useState(true);
  const [areaCode, setAreaCode] = useState('');
  const [callerId, setCallerId] = useState('');
  const [include911, setInclude911] = useState(true);
  const [cnam, setCnam] = useState(false);
  const [result, setResult] = useState<any>(null);

  function toggle(sid: string) {
    const next = new Set(sel);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    setSel(next);
  }

  const configure = useMutation({
    mutationFn: () => {
      const [dtype, ...rest] = String(inbound).split(':');
      const dval = rest.join(':') || null;
      return fJson('/api/integrations/twilio/configure', {
        method: 'POST',
        body: JSON.stringify({
          domain_uuid: sd || undefined,
          trunk_name: trunkName.trim() || undefined,
          number_sids: [...sel],
          inbound: { type: dtype, value: dval },
          outbound: mkOutbound
            ? { create: true, area_code: areaCode.replace(/\D/g, ''), caller_id: callerId, include_911: include911 }
            : { create: false },
          cnam,
        }),
      });
    },
    onSuccess: (r: any) => {
      setResult(r);
      toast(r?.ok ? 'success' : 'error', r?.ok ? `Configured trunk ${r.trunk_sid}` : (r?.error || 'Configure failed'));
      nums.refetch();
    },
    onError: (e: any) => { setResult({ error: String(e.message || e) }); toast('error', String(e.message || e)); },
  });

  function submit() {
    if (!sel.size) return toast('info', 'Select at least one number.');
    if (mkOutbound && (areaCode.replace(/\D/g, '').length !== 3 || !callerId.trim()))
      return toast('info', 'Enter a 3-digit area code and primary caller ID for the outbound rules (or turn them off).');
    setResult(null);
    configure.mutate();
  }

  const destOptions = (dests.data || []).filter((d: any) => d.type !== 'special');

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold flex-1">Twilio Numbers &amp; Auto-setup</h2>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                disabled={nums.isFetching}
                onClick={() => {
                  if (!verified) { toast('info', 'Verify the connection first (Connect & Verify).'); return; }
                  setLoaded(true); nums.refetch();
                }}>
          <Phone size={14} /> {nums.isFetching ? 'Loading…' : loaded ? 'Refresh' : 'Load numbers'}
        </button>
      </div>

      {!loaded && (
        <p className="text-sm text-textSecondary">
          Load this account's numbers, pick which to wire up, and Configure — it provisions the
          Twilio trunk and the matching Stratus trunk, inbound DID routes, and outbound rules.
        </p>
      )}
      {nums.isError && <div className="text-sm text-danger">{String((nums.error as any)?.message || nums.error)}</div>}
      {loaded && !nums.isFetching && !nums.isError && list.length === 0 && (
        <p className="text-sm text-textSecondary">No phone numbers on this account.</p>
      )}

      {list.length > 0 && (
        <>
          <div className="card p-0 overflow-hidden bg-elevated/40">
            <table className="table text-sm">
              <thead><tr><th className="w-8"></th><th>Number</th><th>Name</th><th>Capabilities</th><th>Status</th></tr></thead>
              <tbody>
                {list.map((n: any) => (
                  <tr key={n.sid || n.phone_number} className="hover:bg-elevated cursor-pointer" onClick={() => toggle(n.sid)}>
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={sel.has(n.sid)} onChange={() => toggle(n.sid)} />
                    </td>
                    <td className="font-mono">{n.phone_number}</td>
                    <td>{n.friendly_name}</td>
                    <td className="text-xs text-textSecondary">
                      {[n.capabilities?.voice && 'Voice', n.capabilities?.sms && 'SMS', n.capabilities?.mms && 'MMS'].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td>
                      {n.trunk_sid
                        ? <span className="badge bg-success/20 text-success">On trunk</span>
                        : <span className="badge bg-textSecondary/20">Unassigned</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 border-t border-textSecondary/20 pt-3">
            <div>
              <label className="block text-sm text-textSecondary mb-1">Trunk name</label>
              <input className="input" value={trunkName} onChange={e => setTrunkName(e.target.value)}
                     placeholder="Leave blank to name it after this department" />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">Incoming calls on the selected number(s) ring…</label>
              <select className="input" value={inbound} onChange={e => setInbound(e.target.value)}>
                <option value="end_call">End call (assign routing later)</option>
                {destOptions.map((d: any) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={mkOutbound} onChange={e => setMkOutbound(e.target.checked)} />
              Also create outbound rules (7/10/11-digit + 911) on this trunk
            </label>
            {mkOutbound && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm text-textSecondary mb-1">Area code</label>
                  <input className="input font-mono" value={areaCode} maxLength={5} onChange={e => setAreaCode(e.target.value)} placeholder="989" />
                </div>
                <div>
                  <label className="block text-sm text-textSecondary mb-1">Primary caller ID</label>
                  <input className="input font-mono" value={callerId} onChange={e => setCallerId(e.target.value)} placeholder="(989) 555-1212" />
                </div>
              </div>
            )}
            {mkOutbound && (
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={include911} onChange={e => setInclude911(e.target.checked)} />
                Include a 911 rule
              </label>
            )}

            <label className="inline-flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={cnam} onChange={e => setCnam(e.target.checked)} />
              <span>
                Enable CNAM caller-name lookup on these numbers
                <span className="block text-xs text-textSecondary">Shows the caller's name on incoming calls. Twilio charges about $0.01 per answered call, so it's off by default.</span>
              </span>
            </label>

            <div className="flex items-center gap-2">
              <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={configure.isPending || !sel.size} onClick={submit}>
                <CheckCircle2 size={14} /> {configure.isPending ? 'Configuring…' : `Configure ${sel.size || ''} number${sel.size === 1 ? '' : 's'}`}
              </button>
              <span className="text-xs text-textSecondary">Provisions Twilio + Stratus. Reroutes the selected numbers to this trunk.</span>
            </div>
          </div>

          {result && (
            <div className="card p-3 bg-elevated/40 space-y-2">
              {result.error && !result.steps ? (
                <div className="text-sm text-danger">{result.error}</div>
              ) : (
                <>
                  <div className="text-sm">
                    {result.ok
                      ? <span className="text-success">Done — trunk {result.trunk_sid} ({result.termination_domain}); {result.dids} DID route(s), {result.outbound_rules} outbound rule(s). Origination {result.origination}.</span>
                      : <span className="text-danger">{result.error || 'Some steps failed.'}</span>}
                  </div>
                  <div className="font-mono text-xs space-y-0.5">
                    {(result.steps || []).map((s: any, i: number) => (
                      <div key={i} className={s.ok ? 'text-textSecondary' : 'text-danger'}>
                        [{s.ok ? 'OK' : 'FAIL'}] {s.step}{s.detail ? ` — ${s.detail}` : ''}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
